import React, {
    useContext, useCallback, useState, useEffect
} from 'react';
import _ from 'lodash';
import { BreakpointTrackerContext } from '@jutro/layout';
import { TranslatorContext } from '@jutro/locale';
import { ServiceManager } from '@jutro/services';
import { Chevron } from '@jutro/components';
import { ViewModelServiceContext, ViewModelForm } from 'gw-portals-viewmodel-react';
import { WizardPage, wizardProps } from 'gw-portals-wizard-react';
import { useValidation } from 'gw-portals-validation-react';
import { MockUpUtil } from 'gw-portals-util-js';
import { readViewModelValue } from 'gw-jutro-adapters-react';
import metadata from './PolicyInformationPage.metadata.json5';
import './PolicyInformationPage.messages';
import { PolicyDetailsComponent, CoverageTable, PolicyCommercialPropertyInfoComponent, PolicyLiabilityComponent } from 'gw-capability-gateway-react';
import styles from './PolicyInformationPage.module.scss';

function removeInitialData(submissionVM) {
    // eslint-disable-next-line no-param-reassign
    submissionVM.value = MockUpUtil.cleanUpMockedProperties(
        _.get(submissionVM, 'value'),
        'quote.bop',
        'bindData.contactPhone',
        'bindData.contactEmail',
        'baseData.accountHolder.emailAddress1'
    );
    return submissionVM;
}

function initialiseVM(submissionVM) {
    const vm = removeInitialData(submissionVM);

    vm.bindData.value = vm.bindData.value || {};
    vm.bindData.billingAddress.value = vm.bindData.billingAddress.value
    || vm.baseData.policyAddress.value;
    vm.bindData.contactEmail.value = vm.bindData.contactEmail.value
    || vm.baseData.accountHolder.emailAddress1.value;

    return vm;
}

function PolicyInformationPage(props) {
    const { wizardData: submissionVM, updateWizardData } = props;
    const viewModelService = useContext(ViewModelServiceContext);
    const breakpoint = useContext(BreakpointTrackerContext);
    const [isSameBillingAddress, updateIsSameBillingAddress] = useState(true);
    const [isVmInitialized, updateIsVmInitialized] = useState(false);
    const localeService = ServiceManager.getService('locale-service');
    const { onValidate, isComponentValid } = useValidation('PolicyInformationPage');
    const translator = useContext(TranslatorContext);

    const currencyFormatter = Intl.NumberFormat("en-CA", {style: "currency", currency: "CAD"});
    const [liabilityPremium, updateLiabilityPremium] = useState('');

    useEffect(() => {
        if (!isVmInitialized) {
            let nextSubmissionVM = viewModelService.clone(submissionVM);
            nextSubmissionVM = initialiseVM(nextSubmissionVM);

            if (!_.isEqual(submissionVM.value, nextSubmissionVM.value)) {
                updateWizardData(nextSubmissionVM);
            }
            updateIsVmInitialized(true);
        }
    }, [isVmInitialized, submissionVM, updateWizardData, viewModelService]);

    const handleNext = useCallback(async () => {
        if (isSameBillingAddress) {
            const policyAddress = _.get(submissionVM, 'baseData.policyAddress.value');
            _.set(submissionVM, 'bindData.billingAddress.value', policyAddress);
        }
        return submissionVM;
    }, [isSameBillingAddress, submissionVM]);

    const getCoverables = () => {
        return  [
            {
                coverages: submissionVM.lobData.businessOwners.value.coverages.floaterCoverages,
                name: 'Commercial Property'
            },
            {
                coverages: [],
                name: 'Liability Line'
            }
    
        ]
    }

    const getConstructionDetails = (omiaDetail) => {
        return {
            drainType: translator({id:`typekey.PlumbingType.${omiaDetail.drainType}`}),
            electricalPanelType: translator({id:`typekey.PanelType_EY.${omiaDetail.elecPanelType}`}),
            electricalServiceAmp: translator({id:`typekey.ElectricalServiceAmp_EY.${omiaDetail.elecServAmp}`}),
            hasAdditionalPlumbingSupply: omiaDetail.hasAddPlumbSupply,
            hasHotTub: omiaDetail.hasHotTub,
            hasStandByGenerator: omiaDetail.hasStandByGenerator,
            heatingUpgraded: omiaDetail.hasheatingUpdated,
            numberOfStories: omiaDetail.numStories,
            occupancyType: translator({id:`typekey.DwellingOccupancyType.${omiaDetail.occupancy}`}),
            plumbingType: translator({id:`typekey.PlumbingType.${omiaDetail.addPlumbingType}`}),
            plumbingUpgraded: omiaDetail.hasPlumbingUpdated,
            primaryConstructionClass: omiaDetail.primaryConstructionType,
            primaryFuelType: translator({id:`typekey.HeatingCode_EY.${omiaDetail.primaryFuel}`}),
            primaryHeatingType: translator({id:`typekey.HeatingCode_EY.${omiaDetail.primaryFuel}`}),
            roofMaterial: translator({id:`typekey.HeatingCode_EY.${omiaDetail.primaryFuel}`}),
            roofUpgraded: omiaDetail.hasRoofUpdated,
            secondaryFuelType: omiaDetail.secondaryFuelType,
            secondaryHeatingType: omiaDetail.secondaryHeatingType,
            wiringType: translator({id:`typekey.WiringType.${omiaDetail.wiringType}`}),
            wiringUpdateType: omiaDetail.wiringUpdateType,
            wiringUpgradeYear: omiaDetail.wiringUpgradeYear,
            wiringUpgraded: omiaDetail.wiringUpgraded,
            yearBuild: omiaDetail.yearBuilt
        }
    }

    const getMiscDetails = (omiaDetail) => {
        return {
            burglarAlarmSystem: translator({id:`typekey.BurglarAlarmSystem.${omiaDetail.burglarAlarmSystem}`}),
            fireProtectionGrade_EY: omiaDetail.fireProtectionGrade_EY ? translator({id:`typekey.FireProtectClass.${omiaDetail.fireProtectionGrade_EY}`}) : '',
            fireSmokeSystem: omiaDetail.fireSmokeSystem ? translator({id:`typekey.SmokeAlarms.${omiaDetail.fireSmokeSystem}`}) : '',
            isRoadAccessiblee_EY: omiaDetail.isRoadAccessiblee_EY,
            noOfPonds: omiaDetail.noOfPonds,
            noOfUnits: omiaDetail.numUnits,
            sprinklerSystem: omiaDetail.sprinklerSystem ? translator({id:`typekey.BurglarAlarmSystem.${omiaDetail.sprinklerSystem}`}) : '',
        }
    }

    const getPackageData = () => {
        const locationCoverages = submissionVM.lobData.businessOwners.coverages.locationCoverages.value;
        const cpData = submissionVM.lobData.businessOwners.coverables.value;
        cpData.locations = cpData.locations.map((location) => {
            location.buildings = location.buildings.map((building) => {
                building.coverages = building.baseCoverages.filter((cov)=>{ return cov.selected });
                building.constructionDetails = getConstructionDetails(building.omiaDetail);
                building.yearBuilt = building.omiaDetail.yearBuilt;
                building.miscDetails = getMiscDetails(building.omiaDetail);
                building.homeDetails = {};
                building.discounts = building.bopPropDiscounts || [];
                return building
            });
            location['coverages'] = locationCoverages.filter((locCov)=> {
                return locCov.publicID == location.publicID;
            })[0].coverages.filter((cov)=>{ return cov.selected });
            return location;
        });
        
        return cpData;
    }

    const getLiabilityHeaderSection = (title) => {
        return (isOpen) => (
            <React.Fragment>
                <Chevron isOpen={isOpen} align="right" className={styles.chevronStyle} />
                <h2 className={`${styles.title} ${styles.gwOverrideDefaultMargin}`}>{title}</h2>                
            </React.Fragment>
        );
    }

    const getHeaderSection = (title, title1, title2) => {
        return (isOpen) => (
            <React.Fragment>
                <Chevron isOpen={isOpen} align="right" className={styles.chevronStyle} />
                <h3 className={`${styles.title} ${styles.gwOverrideDefaultMargin}`}>{title}</h3>
                <div className={styles.row} >
                    <div >
                        <h3 className={`${styles.title1} ${styles.gwOverrideDefaultMargin}`}>{title1}</h3>
                    </div>
                    <div><h3 className={`${styles.title1} ${styles.gwOverrideDefaultMargin}`}>{title2}</h3></div>
                </div>
            </React.Fragment>
        );
    }

    const liabilityTitle2 = `Premium:${ currencyFormatter.format(liabilityPremium)}`;

    const overrideProps = {
        '@field': {
            // apply to all fields
            showOptional: true,
            labelPosition: breakpoint === 'desktop' ? 'left' : 'top'
        },
        sameBillingAddress: {
            value: isSameBillingAddress,
            onValueChange: updateIsSameBillingAddress
        },
        billingAddressFields: {
            visible: !isSameBillingAddress
        },
        phoneNumber: {
            countryCode: localeService.getDefaultPhoneCountry()
        },
        policyCommonDetailsForCurrentPeriod: {
            submissionVM: submissionVM
        },
        policyCommercialPropertyInfo: {
            value: getPackageData(),
            coverables: getCoverables(),
            isQuote: true            
        },        
        liabilityDetailsCardSection:{
            header: getHeaderSection("Liability and Extensions", '', liabilityTitle2), //getLiabilityHeaderSection("Liability and Extensions")
        },
        liabilityComponentSection:{
            value: submissionVM.value,
            updateLiabilityPremium: updateLiabilityPremium
        },
    };

    const resolvers = {
        resolveClassNameMap: styles,
        resolveCallbackMap: {
            updateIsSameBillingAddress
        },
        resolveComponentMap: {
            policycommondetailscomponent: PolicyDetailsComponent,
            coveragetablecomponent: CoverageTable,
            policycpinfocomponent: PolicyCommercialPropertyInfoComponent,
            liabilitycomponent: PolicyLiabilityComponent,
            updateLiabilityPremium : updateLiabilityPremium

        },
    };

    const readValue = useCallback(
        (id, path) => {
            return readViewModelValue(metadata.pageContent, submissionVM, id, path, overrideProps);
        },
        [submissionVM, overrideProps]
    );

    return (
        <WizardPage onNext={handleNext} disableNext={!isComponentValid}>
            <ViewModelForm
                uiProps={metadata.pageContent}
                model={submissionVM}
                overrideProps={overrideProps}
                onModelChange={updateWizardData}
                onValidationChange={onValidate}
                callbackMap={resolvers.resolveCallbackMap}                
                classNameMap={resolvers.resolveClassNameMap}            
                componentMap={resolvers.resolveComponentMap}
                resolveValue={readValue}
            />
        </WizardPage>
    );
}

PolicyInformationPage.propTypes = wizardProps;
export default PolicyInformationPage;
